import TimeZoneHelper from '../../../Core/helper/TimeZoneHelper.js';
import ObjectHelper from '../../../Core/helper/ObjectHelper.js';

/**
 * @module Scheduler/model/mixin/TimeZonedDatesMixin
 */

const
    dateFieldsToConvert = {
        startDate      : 1,
        endDate        : 1,
        constraintDate : 1,
        deadlineDate   : 1
    },
    hasTimeZone = r => r.timeZone != null && r.timeZone !== false;

/**
 * This mixin class overrides default Model functionality to provide support for time zone converted dates
 * @mixin
 */
export default Target => class TimeZonedDatesMixin extends Target {

    static $name = 'TimeZonedDatesMixin';

    static fields  = [
        /**
         * The current timeZone this record is converted to. Used internally to keep track of time zone conversions.
         *
         * Can also be used to create a new record with dates in a specific non-local timezone. That is useful for
         * example when replacing a store dataset. That would be interpreted as a new load, and all dates would be
         * converted to the configured timezone.
         *
         * If specifically set to `null` when adding a new record to a Store, the new record's dates will be converted
         * to the Project's configured timezone.
         *
         * If set to `false` on a record, that record will be excluded from timezone conversion.
         *
         * For more information about timezone conversion, se {@link Scheduler.model.ProjectModel#config-timeZone}.
         *
         * This field will not {@link Core.data.field.DataField#config-persist} by default.
         *
         * @field {String|Number|null} timeZone
         * @category Advanced
         */
        {
            name    : 'timeZone',
            persist : false
        }
    ];

    get timeZone() {
        return this.getData('timeZone');
    }

    set timeZone(timeZone) {
        this.setData('timeZone', timeZone);
    }

    static get timeZoneDateFields() {
        return dateFieldsToConvert;
    }

    /**
     * Returns `true` if the field is adjusted by `timeZone` config.
     * @param {String} fieldName Name of a field
     * @returns {Boolean} `true` if the field is adjusted by `timeZone` config.
     * @category Fields
     */
    static isTimeZoneDateField(fieldName) {
        return Boolean(this.timeZoneDateFields[fieldName]);
    }

    setLocalDate(field, date) {
        if (hasTimeZone(this)) {
            date = TimeZoneHelper.toTimeZone(date, this.timeZone);
        }

        this.set(field, date, true);

        return date;
    }

    getLocalDate(field) {
        if (hasTimeZone(this) && this[field] && !isNaN(this[field])) {
            return TimeZoneHelper.fromTimeZone(this[field], this.timeZone);
        }
        return this[field];
    }

    applyChangeset(rawChanges, ...args) {
        // When a sync response arrives from backend, the data will need to be converted to time zone before applied.
        if (hasTimeZone(this)) {
            delete rawChanges.timeZone; // Do not allow timeZone config this way

            for (const field in this.constructor.timeZoneDateFields) {
                if (rawChanges[field]) {
                    const value = this.setLocalDate(field, new Date(rawChanges[field]));
                    // Need to pass the value up the prototype chain in order to get the record field committed
                    rawChanges[field] = value;
                }
            }
        }
        return super.applyChangeset(rawChanges, ...args);
    }

    getFieldPersistentValue(field) {
        if (hasTimeZone(this)) {
            const
                fieldName = field?.field ?? field?.name ?? field,
                self = this.constructor;

            // Used when saving/syncing. Returns local system dates
            if (self.isTimeZoneDateField(fieldName)) {
                const date = this.getLocalDate(fieldName);
                return field.serialize ? field.serialize(date) : date;
            }
        }

        return super.getFieldPersistentValue(field);
    }

    static processFieldData(field, converted, rawValue, data, record) {
        const { timeZone } = record;

        if (converted && timeZone && timeZone !== data.timeZone && this.isTimeZoneDateField(field.name || field)) {
            converted = TimeZoneHelper.toTimeZone(converted, timeZone);
        }

        return converted;
    }

    // Converts current record into a timeZone
    convertToTimeZone(timeZone, ignoreFields) {
        if (this.timeZone === timeZone || this.timeZone === false) {
            return;
        }

        const
            me                  = this,
            metaModified        = { ...me.meta.modified },
            convertFields       = { ...me.constructor.timeZoneDateFields },
            currentTimeZone     = me.timeZone,
            isEngineModel       = Boolean(me.$$);

        // Do not convert start and end dates on task unless manually scheduled
        if (me.isTask && !me.manuallyScheduled) {
            delete convertFields.startDate;
            delete convertFields.endDate;
        }

        ignoreFields?.forEach(field => {
            delete convertFields[field];
        });

        // Collect values
        for (const field in convertFields) {
            // Only convert if field has value
            if (me[field] != null) {

                if (isEngineModel) {
                    // Clear any value cached by engine to be sure we get the correct one
                    me['_' + field] = null;
                }

                convertFields[field] = me[field];

                // If already converted, restore to local system time zone
                if (currentTimeZone != null) {
                    convertFields[field] = me.getLocalDate(field);

                    // Restore value in meta modified as well
                    if (metaModified[field]) {
                        metaModified[field] = TimeZoneHelper.fromTimeZone(metaModified[field], currentTimeZone);
                    }
                }
            }
            else {
                delete convertFields[field];
            }
        }

        // Change time zone
        me.timeZone = timeZone;

        // Set values
        for (const field in convertFields) {
            // Convert and set field date silently
            me.setLocalDate(field, convertFields[field]);
            convertFields[field] = 1; // For clearing changes below

            // Convert value in meta modified as well
            if (timeZone != null && metaModified[field]) {
                metaModified[field] = TimeZoneHelper.toTimeZone(metaModified[field], timeZone);
            }
        }

        // Clear modification metadata
        me.internalClearChanges(true, true, convertFields);

        // If old modification metadata, restore them to record and store
        if (!ObjectHelper.isEmpty(metaModified)) {
            me.meta.modified = metaModified;
            me.stores?.forEach(store => store.modified.add(me));
        }
    }
};
